import { useState, useEffect } from 'react'
import { customerApi } from '../services/api'

export default function ZohoIntegration() {
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [connecting, setConnecting] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState(null)
  const [error, setError] = useState('')

  // Lead email notification state
  const [notifEmail, setNotifEmail] = useState('')
  const [notifSaved, setNotifSaved] = useState(false)
  const [notifLoading, setNotifLoading] = useState(false)
  const [notifError, setNotifError] = useState('')
  const [notifEditing, setNotifEditing] = useState(false)

  useEffect(() => {
    fetchStatus()

    // Check URL params for callback result
    const params = new URLSearchParams(window.location.search)
    if (params.get('zoho_connected') === 'true') {
      setError('')
      fetchStatus()
    } else if (params.get('zoho_error')) {
      setError('Failed to connect Zoho CRM. Please try again.')
    }
  }, [])

  const fetchStatus = async () => {
    try {
      const [zohoRes, notifRes] = await Promise.all([
        customerApi.get('/customer/integrations/zoho/status'),
        customerApi.get('/customer/integrations/lead-notification/status'),
      ])
      setStatus(zohoRes.data)
      if (notifRes.data.email) {
        setNotifEmail(notifRes.data.email)
        setNotifSaved(true)
      }
    } catch (err) {
      console.error('Failed to fetch integration status:', err)
      setStatus({ connected: false, enabled: false })
    } finally {
      setLoading(false)
    }
  }

  const handleConnect = async () => {
    setConnecting(true)
    setError('')
    try {
      const response = await customerApi.get('/customer/integrations/zoho/auth-url')
      // Redirect to Zoho authorization page
      window.location.href = response.data.auth_url
    } catch (err) {
      console.error('Failed to get Zoho auth URL:', err)
      if (err.response?.status === 503) {
        setError('Zoho CRM integration is not configured on the server. Please contact support.')
      } else {
        setError('Failed to initiate Zoho connection. Please try again.')
      }
      setConnecting(false)
    }
  }

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Zoho CRM? New leads will no longer be synced.')) {
      return
    }
    setDisconnecting(true)
    setError('')
    try {
      await customerApi.delete('/customer/integrations/zoho/disconnect')
      setStatus({ connected: false, enabled: false })
      setTestResult(null)
    } catch (err) {
      console.error('Failed to disconnect Zoho:', err)
      setError('Failed to disconnect. Please try again.')
    } finally {
      setDisconnecting(false)
    }
  }

  const handleTestConnection = async () => {
    setTesting(true)
    setTestResult(null)
    try {
      const response = await customerApi.post('/customer/integrations/zoho/test')
      setTestResult(response.data)
    } catch (err) {
      console.error('Failed to test Zoho connection:', err)
      setTestResult({ success: false, message: 'Connection test failed' })
    } finally {
      setTesting(false)
    }
  }

  // --- Lead Email Notification handlers ---

  const handleSaveNotifEmail = async () => {
    if (!notifEmail || !notifEmail.includes('@')) {
      setNotifError('Please enter a valid email address.')
      return
    }
    setNotifLoading(true)
    setNotifError('')
    try {
      await customerApi.post('/customer/integrations/lead-notification/email', { email: notifEmail })
      setNotifSaved(true)
      setNotifEditing(false)
    } catch (err) {
      console.error('Failed to save notification email:', err)
      if (err.response?.status === 422) {
        setNotifError('Please enter a valid email address.')
      } else {
        setNotifError('Failed to save email. Please try again.')
      }
    } finally {
      setNotifLoading(false)
    }
  }

  const handleRemoveNotifEmail = async () => {
    setNotifLoading(true)
    setNotifError('')
    try {
      await customerApi.delete('/customer/integrations/lead-notification/email')
      setNotifEmail('')
      setNotifSaved(false)
      setNotifEditing(false)
    } catch (err) {
      console.error('Failed to remove notification email:', err)
      setNotifError('Failed to remove email. Please try again.')
    } finally {
      setNotifLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900">CRM Integrations</h2>
        <p className="mt-1 text-sm text-gray-600">
          Connect your CRM to automatically create leads from chatbot conversations.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Zoho CRM Card */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              {/* Zoho Logo Placeholder */}
              <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
                <span className="text-red-600 font-bold text-lg">Z</span>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">Zoho CRM</h3>
                <p className="text-sm text-gray-500">
                  Automatically create leads in your Zoho CRM when visitors interact with your chatbot.
                </p>
              </div>
            </div>

            {/* Status Badge */}
            <div className="flex-shrink-0">
              {status?.connected ? (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  Connected
                </span>
              ) : (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-600">
                  <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                  Not Connected
                </span>
              )}
            </div>
          </div>

          {/* Connected State */}
          {status?.connected && (
            <div className="mt-6 space-y-4">
              {/* Connection Info */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-800">
                      {status.org_name ? `Connected to: ${status.org_name}` : 'Zoho CRM is connected'}
                    </p>
                    {status.connected_at && (
                      <p className="text-xs text-green-600 mt-1">
                        Connected since {new Date(status.connected_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* How It Works */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-800 mb-2">How it works</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>1. A visitor chats with your widget and shows buying intent</li>
                  <li>2. The chatbot collects their contact details (name, email, company)</li>
                  <li>3. A lead is automatically created in your Zoho CRM</li>
                  <li>4. Your sales team can follow up from Zoho</li>
                </ul>
              </div>

              {/* Test Result */}
              {testResult && (
                <div className={`border rounded-lg p-4 ${testResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <p className={`text-sm font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                    {testResult.success ? 'Connection test passed!' : testResult.message}
                  </p>
                  {testResult.org_name && (
                    <p className="text-xs text-green-600 mt-1">Organization: {testResult.org_name}</p>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleTestConnection}
                  disabled={testing}
                  className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 disabled:opacity-50 transition-colors"
                >
                  {testing ? 'Testing...' : 'Test Connection'}
                </button>
                <button
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                  className="px-4 py-2 text-sm font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200 disabled:opacity-50 transition-colors"
                >
                  {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                </button>
              </div>
            </div>
          )}

          {/* Not Connected State */}
          {!status?.connected && (
            <div className="mt-6 space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-800 mb-2">What you need</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>- A Zoho CRM account (Free or paid)</li>
                  <li>- Admin/Owner access to your Zoho CRM</li>
                </ul>
                <p className="text-sm text-gray-600 mt-3">
                  Click "Connect Zoho CRM" below. You'll be redirected to Zoho to authorize CognaAssist.
                  No API keys or technical setup needed.
                </p>
              </div>

              <button
                onClick={handleConnect}
                disabled={connecting}
                className="w-full sm:w-auto px-6 py-3 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center justify-center space-x-2"
              >
                {connecting ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    <span>Connecting...</span>
                  </>
                ) : (
                  <>
                    <span>Connect Zoho CRM</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Lead Email Notification Card */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">Lead Email Notifications</h3>
                <p className="text-sm text-gray-500">
                  Receive lead details via email when visitors interact with your chatbot.
                </p>
              </div>
            </div>

            {/* Status Badge */}
            <div className="flex-shrink-0">
              {notifSaved ? (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  Active
                </span>
              ) : (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-600">
                  <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                  Not Set
                </span>
              )}
            </div>
          </div>

          {notifError && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{notifError}</p>
            </div>
          )}

          <div className="mt-6 space-y-4">
            {/* Email saved – show confirmation */}
            {notifSaved && !notifEditing && (
              <>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-green-800">
                    Leads will be sent to: <strong>{notifEmail}</strong>
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    You will receive an email with lead details every time a visitor shares their contact information.
                  </p>
                </div>

                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setNotifEditing(true)}
                    className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
                  >
                    Change Email
                  </button>
                  <button
                    onClick={handleRemoveNotifEmail}
                    disabled={notifLoading}
                    className="px-4 py-2 text-sm font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200 disabled:opacity-50 transition-colors"
                  >
                    {notifLoading ? 'Removing...' : 'Remove'}
                  </button>
                </div>
              </>
            )}

            {/* No email saved or editing – show input form */}
            {(!notifSaved || notifEditing) && (
              <>
                <div className="flex items-center space-x-3">
                  <input
                    type="email"
                    value={notifEmail}
                    onChange={(e) => { setNotifEmail(e.target.value); setNotifError('') }}
                    placeholder="you@company.com"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                  <button
                    onClick={handleSaveNotifEmail}
                    disabled={notifLoading || !notifEmail}
                    className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors whitespace-nowrap"
                  >
                    {notifLoading ? 'Saving...' : 'Save Email'}
                  </button>
                  {notifEditing && (
                    <button
                      onClick={() => { setNotifEditing(false); setNotifError('') }}
                      className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </>
            )}

            {/* Info note */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-600">
                Works independently of Zoho CRM. If both are active, leads will be sent to your email <strong>and</strong> created in Zoho CRM.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
